import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import {
    FaUsers,
    FaSearch,
    FaBuilding,
    FaUserShield,
    FaBan,
    FaCheckCircle,
    FaArrowRight,
    FaPhone,
    FaEnvelope,
    FaSpinner,
    FaCar,
    FaArrowLeft
} from 'react-icons/fa';

const AdminAllUsersPage = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all'); // 'all', 'customer', 'company', 'admin', 'suspended'

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const querySnapshot = await getDocs(query(collection(db, 'users')));
            const usersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sort by createdAt descending if exists
            usersData.sort((a, b) => {
                const timeA = a.createdAt?.seconds || 0;
                const timeB = b.createdAt?.seconds || 0;
                return timeB - timeA;
            });
            setUsers(usersData);
        } catch (error) {
            console.error('Kullanıcılar alınırken hata:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Statistics
    const stats = useMemo(() => {
        const total = users.length;
        const customers = users.filter(u => !u.role || u.role === 'customer' || u.role === 'user').length;
        const companies = users.filter(u => u.role === 'company').length;
        const admins = users.filter(u => u.role === 'admin').length;
        const suspended = users.filter(u => !!u.isSuspended).length;
        return { total, customers, companies, admins, suspended };
    }, [users]);

    // Filtered users based on search & filter tabs
    const filteredUsers = useMemo(() => {
        return users.filter(u => {
            // Role / status filter
            if (roleFilter === 'customer' && u.role !== 'customer' && u.role !== 'user' && u.role) return false;
            if (roleFilter === 'company' && u.role !== 'company') return false;
            if (roleFilter === 'admin' && u.role !== 'admin') return false;
            if (roleFilter === 'suspended' && !u.isSuspended) return false;

            // Search query
            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase();
            const name = (u.displayName || '').toLowerCase();
            const email = (u.email || '').toLowerCase();
            const phone = (u.phoneNumber || '').toLowerCase();
            const id = (u.id || '').toLowerCase();
            return name.includes(q) || email.includes(q) || phone.includes(q) || id.includes(q);
        });
    }, [users, roleFilter, searchQuery]);

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Header with Back Navigation */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center space-x-3">
                        <Link
                            to="/admin"
                            className="p-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-black transition-colors"
                            title="Admin Paneline Dön"
                        >
                            <FaArrowLeft size={16} />
                        </Link>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <FaUsers className="text-blue-600" />
                                <span>Kullanıcı Yönetimi</span>
                            </h1>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Sistemdeki tüm kayıtlı kullanıcıları listeleyin, arayın ve detaylarına erişin.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/admin/print-qr"
                            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
                        >
                            <span>Sticker PDF Yazdır</span>
                        </Link>
                        <Link
                            to="/admin/companies"
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
                        >
                            <FaBuilding size={12} />
                            <span>Şirketler</span>
                        </Link>
                    </div>
                </div>

                {/* Metrics Stats Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                    <button
                        onClick={() => setRoleFilter('all')}
                        className={`p-4 rounded-xl border text-left transition-all ${
                            roleFilter === 'all'
                                ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-500 ring-offset-1'
                                : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-500">Tüm Kullanıcılar</span>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
                    </button>

                    <button
                        onClick={() => setRoleFilter('customer')}
                        className={`p-4 rounded-xl border text-left transition-all ${
                            roleFilter === 'customer'
                                ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-500 ring-offset-1'
                                : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <FaCar size={11} className="text-blue-500" /> Bireysel
                        </span>
                        <p className="text-2xl font-bold text-blue-600 mt-1">{stats.customers}</p>
                    </button>

                    <button
                        onClick={() => setRoleFilter('company')}
                        className={`p-4 rounded-xl border text-left transition-all ${
                            roleFilter === 'company'
                                ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-500 ring-offset-1'
                                : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <FaBuilding size={11} className="text-purple-500" /> Rent A Car
                        </span>
                        <p className="text-2xl font-bold text-purple-600 mt-1">{stats.companies}</p>
                    </button>

                    <button
                        onClick={() => setRoleFilter('admin')}
                        className={`p-4 rounded-xl border text-left transition-all ${
                            roleFilter === 'admin'
                                ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-500 ring-offset-1'
                                : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <FaUserShield size={11} className="text-rose-500" /> Yönetici
                        </span>
                        <p className="text-2xl font-bold text-rose-600 mt-1">{stats.admins}</p>
                    </button>

                    <button
                        onClick={() => setRoleFilter('suspended')}
                        className={`p-4 rounded-xl border text-left transition-all col-span-2 sm:col-span-1 ${
                            roleFilter === 'suspended'
                                ? 'bg-red-50 border-red-300 ring-2 ring-red-500 ring-offset-1'
                                : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <FaBan size={11} className="text-red-500" /> Dondurulan
                        </span>
                        <p className="text-2xl font-bold text-red-600 mt-1">{stats.suspended}</p>
                    </button>
                </div>

                {/* Search & Filter Bar */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="relative w-full md:w-96">
                        <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="İsim, e-posta, telefon veya UID ile ara..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
                            >
                                Temizle
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0">
                        {[
                            { key: 'all', label: 'Tümü' },
                            { key: 'customer', label: 'Bireysel' },
                            { key: 'company', label: 'Rent A Car' },
                            { key: 'admin', label: 'Admin' },
                            { key: 'suspended', label: 'Dondurulan' }
                        ].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setRoleFilter(tab.key)}
                                className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${
                                    roleFilter === tab.key
                                        ? 'bg-black text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="py-20 text-center text-gray-500">
                            <FaSpinner className="animate-spin text-3xl text-blue-600 mx-auto mb-3" />
                            <p className="text-sm">Kullanıcılar yükleniyor...</p>
                        </div>
                    ) : filteredUsers.length === 0 ? (
                        <div className="py-20 text-center text-gray-500">
                            <FaUsers className="text-4xl text-gray-300 mx-auto mb-2" />
                            <p className="font-semibold text-gray-700">Kullanıcı bulunamadı</p>
                            <p className="text-xs text-gray-400 mt-1">Arama kriterlerinizi değiştirmeyi deneyin.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr className="bg-gray-50/80 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        <th className="px-6 py-4">Kullanıcı</th>
                                        <th className="px-6 py-4">İletişim</th>
                                        <th className="px-6 py-4">Rol</th>
                                        <th className="px-6 py-4">Hesap Durumu</th>
                                        <th className="px-6 py-4">Kayıt Tarihi</th>
                                        <th className="px-6 py-4 text-right">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-sm">
                                    {filteredUsers.map((u) => {
                                        const isSuspended = !!u.isSuspended;
                                        const role = u.role || 'customer';

                                        return (
                                            <tr
                                                key={u.id}
                                                onClick={() => navigate(`/admin/user/${u.id}`)}
                                                className="hover:bg-blue-50/40 cursor-pointer transition-colors"
                                            >
                                                {/* User Info */}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-gray-700 to-gray-900 text-white font-bold flex items-center justify-center text-sm shadow-sm flex-shrink-0">
                                                            {u.displayName ? u.displayName.charAt(0).toUpperCase() : 'U'}
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-gray-900 hover:text-blue-600 transition-colors">
                                                                {u.displayName || 'İsimsiz'}
                                                            </div>
                                                            <div className="text-[11px] text-gray-400 font-mono">
                                                                {u.id.substring(0, 10)}...
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Contact & Verification */}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-1.5 text-xs text-gray-700">
                                                            <FaEnvelope size={11} className="text-gray-400" />
                                                            <span>{u.email || '—'}</span>
                                                            {u.isEmailVerified && (
                                                                <FaCheckCircle size={10} className="text-green-500" title="E-Posta Onaylı" />
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                                            <FaPhone size={11} className="text-gray-400" />
                                                            <span>{u.phoneNumber || '—'}</span>
                                                            {u.isPhoneVerified && (
                                                                <FaCheckCircle size={10} className="text-green-500" title="Telefon Onaylı" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Role Badge */}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                                                        role === 'company'
                                                            ? 'bg-purple-100 text-purple-700'
                                                            : role === 'admin'
                                                            ? 'bg-rose-100 text-rose-700'
                                                            : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                        {role === 'company' ? 'Rent A Car' : role === 'admin' ? 'Yönetici' : 'Bireysel'}
                                                    </span>
                                                </td>

                                                {/* Status Badge */}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {isSuspended ? (
                                                        <span className="px-2.5 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full inline-flex items-center gap-1">
                                                            <FaBan size={10} /> Donduruldu
                                                        </span>
                                                    ) : (
                                                        <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full inline-flex items-center gap-1">
                                                            <FaCheckCircle size={10} /> Aktif
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Date */}
                                                <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                                    {u.createdAt?.seconds
                                                        ? new Date(u.createdAt.seconds * 1000).toLocaleDateString('tr-TR')
                                                        : '—'}
                                                </td>

                                                {/* Action Button */}
                                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                                    <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800">
                                                        <span>Detay / Yönet</span>
                                                        <FaArrowRight size={10} />
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AdminAllUsersPage;